import React from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuthContext } from './providers';
import { captureReferralFromUrl, referralService } from './services/Profile/referralService';
import { RateLimitProvider } from './providers/RateLimitProvider';
import { SubscriptionProvider } from './providers/SubscriptionProvider';
import { ChatRoomThemeProvider } from './contexts/ChatRoomThemeContext';
import { ProtectedRoute } from './components';
import { Login } from './pages/Login/Login';
import { Registration } from './pages/User Registration/Registration';
import { PrivacyPolicy } from './pages/Privacy Policy/PrivacyPolicy';
import { TermsOfService } from './pages/Terms of Service/TermsOfService';
import { ResetPassword } from './pages/Reset Password';
import { Support } from './pages/Support';
import SplashScreen from './components/splash/SplashScreen';
import { useRoleBasedNavigation } from './hooks/auth/useRoleBasedNavigation';
import {
  ProfileSetup,
  ProfilePreviewPage,
  ProfileViewPage
} from './pages/Profile';
import { Onboarding } from './pages/Onboarding';
import { OnboardingGate } from './components/onboarding/OnboardingGate';
import { Messaging } from './pages/Messaging';
import {
  HeartGamesHub,
  GameSelection,
  GamePlay,
  HeartProfile,
  HeartCompatibility
} from './pages/HeartGames';
import { SwipeHub } from './pages/Swipe';
import { Settings } from './pages/Settings';
import { Safety } from './pages/Safety/Safety';
import { SocialFeed } from './pages/SocialFeed';
import { CommunityChatRooms } from './pages/CommunityChatRooms';
import { CommunityFeed } from './pages/CommunityChatRooms/CommunityFeed';
import { PostDetail } from './pages/CommunityChatRooms/PostDetail';
import { CreatePostPage } from './pages/CommunityChatRooms/CreatePostPage';
import { BottomNavigationWrapper, ScrollToTop } from './components/layout';
import { SwipeModal } from './components/Swipe/SwipeModal';
import { UnderConstruction } from './components/shared';
import { useShouldShowNav } from './hooks';
import styled from 'styled-components';
import { theme } from './styles/theme';

// App Container with persistent navigation padding.
// Padding is only reserved when the nav is actually visible on the current
// route (see useShouldShowNav) — otherwise pages like the root splash screen
// or onboarding (where nav is intentionally hidden) end up with an empty
// gutter reserved for a sidebar/bar that was never rendered.
const AppContainer = styled.div<{ $showNav: boolean }>`
  background: transparent;
  padding-bottom: ${({ $showNav }) => ($showNav ? '70px' : '0')}; /* Account for persistent bottom navigation */

  @media (min-width: ${theme.breakpoints.tablet}) {
    /* Bottom tab bar hides at this breakpoint, so no bottom padding needed */
    padding-bottom: 0;
  }

  @media (min-width: calc(${theme.breakpoints.tablet} + 1px)) {
    /* Account for the persistent desktop sidebar, only when it's shown */
    padding-left: ${({ $showNav }) => ($showNav ? '88px' : '0')};
  }
`;

// Renders AppContainer with the correct nav-aware padding.
const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const showNav = useShouldShowNav();
  return (
    <AppContainer className="App" $showNav={showNav}>
      {children}
    </AppContainer>
  );
};

// Wrapper component to handle role-based navigation
// Read ?ref= before React renders anything, so a referral survives whatever
// redirect the router performs first — a login bounce would otherwise strip the
// query string before any component had a chance to see it.
captureReferralFromUrl();

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthContext();
  const [isSwipeModalOpen, setIsSwipeModalOpen] = React.useState(false);
  useRoleBasedNavigation(); // This will handle role-based redirects

  // Attribute a pending referral once an account exists to attach it to. Runs
  // on every sign-in rather than only at registration: someone can open the
  // link, sign up on another device, and come back — and the claim is a no-op
  // for anyone already attributed.
  React.useEffect(() => {
    if (isAuthenticated) {
      referralService.claimPendingReferral();
    }
  }, [isAuthenticated]);

  const handleRegistrationComplete = (_user: any) => {
    navigate('/social-feed');
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    // Above the route tree so every page reads one subscription state — the
    // pricing modal, the profile menu and the feature gates can no longer
    // disagree about what plan you are on.
    <SubscriptionProvider>
    <RateLimitProvider>
        <ChatRoomThemeProvider>
          {/* 
            UNCOMMENT THE LINE BELOW TO ENABLE "UNDER CONSTRUCTION" MODE 
            THIS WILL COVER ALL PUBLIC AND PROTECTED PAGES
          */}
          {/* <UnderConstruction /> */}
          <ScrollToTop />
          {/* Wraps the whole route table rather than individual routes: the
              point is that there is no way out of onboarding, and a per-route
              guard is only ever as complete as the last person to remember it.
              Exempt paths are listed inside the gate. */}
          <OnboardingGate>
          <AppShell>
            <Routes>
              {/* Public Routes - Redirect authenticated users to dashboard */}
              <Route
                path="/"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <SplashScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/login"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <Login />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/registration"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <Registration
                      onRegistrationComplete={handleRegistrationComplete}
                      onBackToLogin={handleBackToLogin}
                    />
                  </ProtectedRoute>
                }
              />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/support" element={<Support />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Protected Routes - Require authentication */}

              {/* Onboarding Route - Require authentication */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />

              {/* Profile Routes - Require authentication */}
              <Route
                path="/profile/setup"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <ProfileSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/preview"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <ProfilePreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/view"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <ProfileViewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/view/:userId"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <ProfileViewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messaging"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <Messaging />
                  </ProtectedRoute>
                }
              />

              {/* Heart Games Routes - Require authentication */}
              <Route
                path="/heart-games"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <HeartGamesHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/heart-games/:gameId"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <GameSelection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/heart-games/:gameId/play"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <GamePlay />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/heart-games/:gameId/results"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <HeartProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/heart-games/profile"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <HeartProfile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/heart-games/:gameId/compatibility"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <HeartCompatibility />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <Settings />
                  </ProtectedRoute>
                }
              />

              {/* Blocked users and submitted reports. The page has existed since
                  the safety features shipped and was never routed — complete,
                  working, and unreachable. */}
              <Route
                path="/safety"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <Safety />
                  </ProtectedRoute>
                }
              />

              {/* Swipe Interface Routes - Redirect to social-feed (swipe now handled by modal) */}
              <Route
                path="/swipe"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <Navigate to="/social-feed" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/swipe-poker"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <Navigate to="/social-feed" replace />
                  </ProtectedRoute>
                }
              />

              {/* Social Feed Routes - Require authentication */}
              <Route
                path="/social-feed"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <SocialFeed />
                  </ProtectedRoute>
                }
              />

              {/* Community Chat Rooms Routes - Require authentication */}
              <Route
                path="/community-chat-rooms"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <CommunityChatRooms />
                  </ProtectedRoute>
                }
              />
              {/* Community Feed Routes */}
              <Route
                path="/community-chat-rooms/:personaId"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <CommunityFeed />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/community-chat-rooms/room/:roomId"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <CommunityFeed />
                  </ProtectedRoute>
                }
              />
              {/* Post Detail Routes */}
              <Route
                path="/community-chat-rooms/:personaId/post/:postId"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <PostDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/community-chat-rooms/room/:roomId/post/:postId"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <PostDetail />
                  </ProtectedRoute>
                }
              />
              {/* Create Post Routes */}
              <Route
                path="/community-chat-rooms/:personaId/create"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <CreatePostPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/community-chat-rooms/room/:roomId/create"
                element={
                  <ProtectedRoute allowedRoles={["user", "premium", "admin"]}>
                    <CreatePostPage />
                  </ProtectedRoute>
                }
              />
              {/* Catch all unrecognized routes and redirect to the root splash/welcome page */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            {/* Swipe Modal - Full Screen Immersive Experience */}
            <SwipeModal
              isOpen={isSwipeModalOpen}
              onClose={() => setIsSwipeModalOpen(false)}
            />

            {/* Persistent Bottom Navigation */}
            <BottomNavigationWrapper
              onSwipeModeToggle={(enabled) => {
                if (enabled) {
                  setIsSwipeModalOpen(true);
                }
              }}
            />
          </AppShell>
          </OnboardingGate>
        </ChatRoomThemeProvider>
    </RateLimitProvider>
    </SubscriptionProvider>
  );
}

// AuthProvider wraps AppContent rather than being rendered by it, so that
// AppContent's own useRoleBasedNavigation() sits inside the provider and can
// read the shared auth state. Previously AppContent rendered <AuthProvider>
// as its child, which put its hook outside the context it was creating —
// forcing that hook onto a private useAuth() instance with its own async
// session check. Order relative to the other providers is unchanged.
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
